// ============================================================
//  types/ytmusic.ts  — 프로젝트 전체 타입 정의 (v3)
// ============================================================

// ── 트랙 정보 ────────────────────────────────────────────────
export interface Track {
  title: string;
  artist: string;
  thumbnail: string;
  videoId: string;
  url: string;
  timestamp: number;
}

// ── 재생 상태 ────────────────────────────────────────────────
export interface PlayState {
  isPlaying: boolean;
  currentTrack: Track | null;
  pendingCount: number;
}

// ── 큐 아이템 ────────────────────────────────────────────────
export type QueueItemType = 'replace' | 'add';

export interface QueueItem {
  videoId: string;
  reason: string;
  type: QueueItemType;
}

// ── 로그 엔트리 ──────────────────────────────────────────────
export type LogType = 'info' | 'track' | 'queue' | 'warn' | 'error';

export interface LogEntry {
  id: number;
  time: string;
  type: LogType;
  message: string;
  data?: unknown;
}

// ── 트랙 교체 옵션 ───────────────────────────────────────────
export interface ReplaceTrackOptions {
  immediate?: boolean;
  reason?: string;
}

export interface QueueOptions {
  reason?: string;
  playFirst?: boolean;
}

// ── useYTMusicMonitor 훅 반환 타입 ───────────────────────────
export interface UseYTMusicMonitorReturn {
  playState: PlayState;
  logs: LogEntry[];
  isReady: boolean;
  replaceNextTrack: (videoId: string, options?: ReplaceTrackOptions) => void;
  addToQueue: (videoIds: string | string[], options?: QueueOptions) => void;
  replaceQueue: (videoIds: string[], options?: QueueOptions) => void;
  clearQueue: () => void;
  addLog: (type: LogType, message: string, data?: unknown) => LogEntry;
}

// ── 샘플 트랙 ────────────────────────────────────────────────
export interface SampleTrack {
  id: string;
  title: string;
}

// ── window.__ytMonitor 전역 타입 확장 ────────────────────────
export interface YTMonitorAPI {
  version: string;
  getCurrentTrack: () => Track | null;
  getPlayState: () => { isPlaying: boolean; currentTrack: Track | null; pendingCount: number };
  getPendingQueue: () => QueueItem[];
  replaceNextTrack: (videoIdOrUrl: string, options?: ReplaceTrackOptions) => boolean;
  addToQueue: (videoIdOrUrls: string | string[], options?: QueueOptions) => number;
  replaceQueue: (videoIds: string[], options?: QueueOptions) => number;
  clearPendingQueue: () => void;
  on: (event: string, callback: (data: unknown) => void) => void;
  off: (event: string, callback: (data: unknown) => void) => void;
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
}

// ============================================================
//  YouTube IFrame Player API 공식 타입 정의
//  https://developers.google.com/youtube/iframe_api_reference
// ============================================================

/** YT.PlayerState 상수 */
export enum YTPlayerState {
  UNSTARTED = -1,
  ENDED     =  0,
  PLAYING   =  1,
  PAUSED    =  2,
  BUFFERING =  3,
  CUED      =  5,
}

/** YT.Player 생성자 옵션 */
export interface YTPlayerOptions {
  width?: number | string;
  height?: number | string;
  videoId?: string;
  playerVars?: YTPlayerVars;
  events?: YTPlayerEvents;
}

/**
 * playerVars — 임베드 URL 파라미터 전체 목록
 * https://developers.google.com/youtube/player_parameters
 */
export interface YTPlayerVars {
  /** 자동재생 (1=on). 브라우저 정책상 mute=1 필요 */
  autoplay?: 0 | 1;
  /** 컨트롤 바 표시 (0=숨김, 1=표시, 2=Flash 지원 deprecated) */
  controls?: 0 | 1 | 2;
  /** 반복 재생 (1=on) */
  loop?: 0 | 1;
  /** 음소거 (1=on) */
  mute?: 0 | 1;
  /** 관련 동영상 표시 (0=숨김, 1=표시) */
  rel?: 0 | 1;
  /** 정보 바(제목 등) 표시 (0=숨김, 1=표시) */
  showinfo?: 0 | 1;
  /** 시작 시간(초) */
  start?: number;
  /** 종료 시간(초) */
  end?: number;
  /** 재생목록 ID (loop=1과 함께 사용) */
  list?: string;
  /** list 타입 ('playlist' | 'search' | 'user_uploads') */
  listType?: 'playlist' | 'search' | 'user_uploads';
  /** playsinline: iOS에서 인라인 재생 (1=on) */
  playsinline?: 0 | 1;
  /** 모드: 'transparent' | 'opaque' */
  wmode?: 'transparent' | 'opaque';
  /** 자막 언어 코드 (예: 'ko', 'en') */
  cc_lang_pref?: string;
  /** 자막 자동 로드 (1=on) */
  cc_load_policy?: 1;
  /** 색상 테마: 'red' | 'white' */
  color?: 'red' | 'white';
  /** 키보드 컨트롤 비활성화 (1=비활성) */
  disablekb?: 0 | 1;
  /** JS API 활성화 (1=on, 필수) */
  enablejsapi?: 0 | 1;
  /** fullscreen 버튼 표시 (0=숨김, 1=표시) */
  fs?: 0 | 1;
  /** 인터페이스 언어 코드 */
  hl?: string;
  /** 자막 설정 패널 기본 표시 (1=on) */
  iv_load_policy?: 1 | 3;
  /** origin: 보안 JS API에서 origin 도메인 */
  origin?: string;
  /** widget_referrer: iframe 출처 추적 */
  widget_referrer?: string;
  /** playlist: 동영상 ID 쉼표 구분 목록 */
  playlist?: string;
  /** 유튜브 로고 최소화 (1=on, deprecated but still functional) */
  modestbranding?: 0 | 1;
  /** 컨트롤 자동 숨김 (0=항상 표시, 1=자동 숨김) */
  autohide?: 0 | 1;
}

/** YT.Player 이벤트 콜백 */
export interface YTPlayerEvents {
  onReady?: (event: YTPlayerEvent) => void;
  onStateChange?: (event: YTPlayerStateChangeEvent) => void;
  onPlaybackQualityChange?: (event: YTPlayerPlaybackQualityEvent) => void;
  onPlaybackRateChange?: (event: YTPlayerPlaybackRateEvent) => void;
  onError?: (event: YTPlayerErrorEvent) => void;
  onApiChange?: (event: YTPlayerEvent) => void;
}

export interface YTPlayerEvent {
  target: YTPlayer;
  data?: unknown;
}

export interface YTPlayerStateChangeEvent extends YTPlayerEvent {
  data: YTPlayerState;
}

export interface YTPlayerPlaybackQualityEvent extends YTPlayerEvent {
  data: YTPlaybackQuality;
}

export interface YTPlayerPlaybackRateEvent extends YTPlayerEvent {
  data: number;
}

export interface YTPlayerErrorEvent extends YTPlayerEvent {
  data: YTPlayerError;
}

/** 재생 품질 */
export type YTPlaybackQuality =
  | 'small' | 'medium' | 'large' | 'hd720' | 'hd1080' | 'highres' | 'default';

/** 에러 코드 */
export enum YTPlayerError {
  INVALID_PARAM      = 2,
  HTML5_ERROR        = 5,
  NOT_FOUND          = 100,
  NOT_EMBEDDABLE     = 101,
  NOT_EMBEDDABLE_ALT = 150,
}

/**
 * YT.Player 인스턴스 인터페이스
 * https://developers.google.com/youtube/iframe_api_reference#Functions
 */
export interface YTPlayer {
  // ── 큐 / 로드 ────────────────────────────────────────────
  /** 단일 영상 큐 (자동재생 없음) */
  cueVideoById(videoId: string, startSeconds?: number, suggestedQuality?: YTPlaybackQuality): void;
  cueVideoById(options: { videoId: string; startSeconds?: number; endSeconds?: number; suggestedQuality?: YTPlaybackQuality }): void;

  /** 단일 영상 로드 (자동재생) */
  loadVideoById(videoId: string, startSeconds?: number, suggestedQuality?: YTPlaybackQuality): void;
  loadVideoById(options: { videoId: string; startSeconds?: number; endSeconds?: number; suggestedQuality?: YTPlaybackQuality }): void;

  /** URL로 큐 */
  cueVideoByUrl(mediaContentUrl: string, startSeconds?: number, suggestedQuality?: YTPlaybackQuality): void;

  /** URL로 로드 */
  loadVideoByUrl(mediaContentUrl: string, startSeconds?: number, suggestedQuality?: YTPlaybackQuality): void;

  /** 재생목록 큐 */
  cuePlaylist(playlist: string | string[], index?: number, startSeconds?: number, suggestedQuality?: YTPlaybackQuality): void;
  cuePlaylist(options: { listType: string; list: string; index?: number; startSeconds?: number; suggestedQuality?: YTPlaybackQuality }): void;

  /** 재생목록 로드 */
  loadPlaylist(playlist: string | string[], index?: number, startSeconds?: number, suggestedQuality?: YTPlaybackQuality): void;
  loadPlaylist(options: { listType: string; list: string; index?: number; startSeconds?: number; suggestedQuality?: YTPlaybackQuality }): void;

  // ── 재생 제어 ────────────────────────────────────────────
  playVideo(): void;
  pauseVideo(): void;
  stopVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  clearVideo(): void;

  // ── 큐 탐색 ──────────────────────────────────────────────
  nextVideo(): void;
  previousVideo(): void;
  playVideoAt(index: number): void;

  // ── 볼륨 / 음소거 ────────────────────────────────────────
  mute(): void;
  unMute(): void;
  isMuted(): boolean;
  setVolume(volume: number): void;
  getVolume(): number;

  // ── 재생 속도 ────────────────────────────────────────────
  setPlaybackRate(suggestedRate: number): void;
  getPlaybackRate(): number;
  getAvailablePlaybackRates(): number[];

  // ── 재생 품질 ────────────────────────────────────────────
  setPlaybackQuality(suggestedQuality: YTPlaybackQuality): void;
  getPlaybackQuality(): YTPlaybackQuality;
  getAvailableQualityLevels(): YTPlaybackQuality[];

  // ── 상태 조회 ────────────────────────────────────────────
  getCurrentTime(): number;
  getDuration(): number;
  getVideoLoadedFraction(): number;
  getPlayerState(): YTPlayerState;
  getVideoUrl(): string;
  getVideoEmbedCode(): string;

  // ── 재생목록 설정 ────────────────────────────────────────
  /** 재생목록 반복 재생 설정 */
  setLoop(loopPlaylists: boolean): void;
  /** 재생목록 셔플 설정 */
  setShuffle(shufflePlaylist: boolean): void;

  // ── 재생목록 조회 ────────────────────────────────────────
  getPlaylist(): string[];
  getPlaylistIndex(): number;

  // ── iframe 접근 ──────────────────────────────────────────
  getIframe(): HTMLIFrameElement;
  destroy(): void;

  // ── 자막 ─────────────────────────────────────────────────
  setOption(module: string, option: string, value: unknown): void;
  getOption(module: string, option: string): unknown;
}

/** window.YT 네임스페이스 */
export interface YTNamespace {
  Player: new (elementId: string | HTMLElement, options: YTPlayerOptions) => YTPlayer;
  PlayerState: typeof YTPlayerState;
  PlayerError: typeof YTPlayerError;
}


// ── 구형 monitor 훅 호환 타입 (useYTMusicMonitor.ts용) ────────
export interface YTMusicMessage {
  type: string;
  payload?: unknown;
}
export interface TrackChangedPayload {
  prev: Track | null;
  current: Track;
}
export interface PlaybackPayload {
  track: Track | null;
}
export interface QueueInjectingPayload extends QueueItem {}
export type CommandType = 'replaceNextTrack' | 'addToQueue' | 'replaceQueue' | 'clearQueue';
